import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const QuoteModal = ({ quote, onClose, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    quote_number: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    project_type: 'zonnepanelen',
    quote_amount: '',
    status: 'concept',
    quote_date: new Date()?.toISOString()?.split('T')?.[0],
    valid_until: '',
    description: '',
    notes: '',
  });

  useEffect(() => {
    if (quote) {
      setFormData({
        quote_number: quote?.quote_number || '',
        customer_name: quote?.customer_name || '',
        customer_email: quote?.customer_email || '',
        customer_phone: quote?.customer_phone || '',
        project_type: quote?.project_type || 'zonnepanelen',
        quote_amount: quote?.quote_amount || '',
        status: quote?.status || 'concept',
        quote_date: quote?.quote_date || new Date()?.toISOString()?.split('T')?.[0],
        valid_until: quote?.valid_until || '',
        description: quote?.description || '',
        notes: quote?.notes || '',
      });
    } else {
      // Generate quote number for new quote
      generateQuoteNumber();
    }
  }, [quote]);

  const generateQuoteNumber = async () => {
    try {
      const { count } = await supabase?.from('provider_quotes')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id);

      const year = new Date()?.getFullYear();
      const number = String((count || 0) + 1)?.padStart(3, '0');
      setFormData(prev => ({ ...prev, quote_number: `OFF-${year}-${number}` }));
    } catch (error) {
      console.error('Error generating quote number:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const quoteData = {
        ...formData,
        provider_id: user?.id,
        quote_amount: parseFloat(formData?.quote_amount),
      };

      if (quote) {
        // Update existing quote
        const { error } = await supabase?.from('provider_quotes')?.update(quoteData)?.eq('id', quote?.id)?.eq('provider_id', user?.id);

        if (error) throw error;
      } else {
        // Create new quote
        const { error } = await supabase?.from('provider_quotes')?.insert([quoteData]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Fout bij opslaan van offerte');
    } finally {
      setLoading(false);
    }
  };

  const projectTypeOptions = [
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'isolatie', label: 'Isolatie' },
    { value: 'warmtepomp', label: 'Warmtepomp' },
    { value: 'ventilatie', label: 'Ventilatie' },
    { value: 'thuisbatterij', label: 'Thuisbatterij' },
    { value: 'waterontharder', label: 'Waterontharder' },
    { value: 'overig', label: 'Overig' },
  ];

  const statusOptions = [
    { value: 'concept', label: 'Concept' },
    { value: 'verstuurd', label: 'Verstuurd' },
    { value: 'in_overweging', label: 'In Overweging' },
    { value: 'gewonnen', label: 'Gewonnen' },
    { value: 'verloren', label: 'Verloren' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {quote ? 'Offerte Bewerken' : 'Nieuwe Offerte'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Offertenummer"
              value={formData?.quote_number}
              onChange={(e) => handleChange('quote_number', e?.target?.value)}
              required
              disabled
            />
            <Select
              label="Status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleChange('status', value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Klantnaam"
              value={formData?.customer_name}
              onChange={(e) => handleChange('customer_name', e?.target?.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              value={formData?.customer_email}
              onChange={(e) => handleChange('customer_email', e?.target?.value)}
              required
            />
          </div>

          <Input
            label="Telefoonnummer"
            value={formData?.customer_phone}
            onChange={(e) => handleChange('customer_phone', e?.target?.value)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Type Verduurzaming"
              options={projectTypeOptions}
              value={formData?.project_type}
              onChange={(value) => handleChange('project_type', value)}
              required
            />
            <Input
              label="Offertebedrag (€)"
              type="number"
              step="0.01"
              value={formData?.quote_amount}
              onChange={(e) => handleChange('quote_amount', e?.target?.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Offertedatum"
              type="date"
              value={formData?.quote_date}
              onChange={(e) => handleChange('quote_date', e?.target?.value)}
              required
            />
            <Input
              label="Geldig tot"
              type="date"
              value={formData?.valid_until}
              onChange={(e) => handleChange('valid_until', e?.target?.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Beschrijving</label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Beschrijving van de offerte..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Notities (intern)</label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px]"
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="Interne notities..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} type="button">
              Annuleren
            </Button>
            <Button type="submit" loading={loading}>
              {quote ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;